/**
 * PersistenceIndicator — subtle, truthful "Saving…/Saved/Not persisted"
 * status glyph that mirrors the ACTUAL API state of the backend
 * conversation row.
 *
 * Honest-empty-states policy:
 *   • Only renders text that reflects the LAST observed API response.
 *   • Never shows "Saved" optimistically — the hook only flips to
 *     "saved" after every dirty turn has landed a 200 from
 *     PUT /api/sessions/{id}/turns/{turn_index}.
 *   • "Not persisted" is a soft warning tone, not an alarm. It stays
 *     visible until the session ends (per Founder brief).
 *
 * States mapped from useRealtimeSession().persistenceState:
 *   "idle"         → nothing rendered (session not live yet).
 *   "creating"     → dashed dot + "Starting…"
 *   "saving"       → pulsing dot + "Saving…"
 *   "saved"        → solid mint dot + "Saved"
 *   "not_persisted"→ amber dot + "Not persisted" (+ optional tooltip
 *                    with the underlying error / signed-out reason)
 */
import { useMemo } from "react";

const STATES = {
    creating: {
        label: "Starting…",
        tone: "cyan",
        pulse: true,
        testId: "persistence-indicator-creating",
    },
    saving: {
        label: "Saving…",
        tone: "cyan",
        pulse: true,
        testId: "persistence-indicator-saving",
    },
    saved: {
        label: "Saved",
        tone: "mint",
        pulse: false,
        testId: "persistence-indicator-saved",
    },
    not_persisted: {
        label: "Not persisted",
        tone: "amber",
        pulse: false,
        testId: "persistence-indicator-not-persisted",
    },
};

const TONE_STYLES = {
    cyan:  { color: "#00E5FF", text: "text-qoryn-textDim" },
    mint:  { color: "#5EF7C6", text: "text-qoryn-textDim" },
    amber: { color: "#F5B75B", text: "text-yellow-300/90" },
};

export default function PersistenceIndicator({
    state,
    error = null,
    sessionId = null,
}) {
    const cfg = useMemo(() => STATES[state] ?? null, [state]);
    if (!cfg) return null;
    const tone = TONE_STYLES[cfg.tone];
    const title = state === "not_persisted"
        ? (error
            ? `Not persisted — ${error}`
            : "This session is not being saved to your account.")
        : (sessionId ? `Session ${sessionId.slice(0, 8)}` : cfg.label);
    return (
        <div
            data-testid={cfg.testId}
            data-persistence-state={state}
            title={title}
            className="flex items-center gap-1.5 qoryn-panel rounded-full px-2.5 py-1 shrink-0"
        >
            <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                    cfg.pulse ? "animate-pulse" : ""
                }`}
                style={{
                    backgroundColor: tone.color,
                    boxShadow: `0 0 6px ${tone.color}`,
                }}
            />
            <span
                className={`font-mono-ui text-[10px] tracking-widest uppercase ${tone.text}`}
            >
                {cfg.label}
            </span>
        </div>
    );
}
